const HeroImageCollage = () => {
  return (
    <div className="flex items-center justify-center py-6 sm:py-8 hero-collage-wrapper w-full max-w-[550px] mx-auto">
      <div className="relative w-full aspect-[1.1] sm:aspect-[1.15] md:aspect-[1.2]">

        {/* Decorative Grid Dotted Pattern (Top-Left) */}
        <div className="absolute top-[2%] left-[2%] w-24 h-24 text-blue-400/30 opacity-60 pointer-events-none">
          <svg width="100%" height="100%" fill="currentColor">
            <pattern id="grid-dots-1" x="0" y="0" width="16" height="16" patternUnits="userSpaceOnUse">
              <circle cx="2" cy="2" r="2" />
            </pattern>
            <rect width="100%" height="100%" fill="url(#grid-dots-1)" />
          </svg>
        </div>

        {/* Decorative Grid Dotted Pattern (Bottom-Center) */}
        <div className="absolute -bottom-[2%] left-[35%] w-28 h-12 text-blue-300/30 opacity-60 pointer-events-none">
          <svg width="100%" height="100%" fill="currentColor">
            <pattern id="grid-dots-2" x="0" y="0" width="16" height="16" patternUnits="userSpaceOnUse">
              <circle cx="2" cy="2" r="2" />
            </pattern>
            <rect width="100%" height="100%" fill="url(#grid-dots-2)" />
          </svg>
        </div>

        {/* Floating Wireframe Icon (Top-Left) */}
        <div className="absolute top-[12%] left-[4%] z-10 w-12 h-12 bg-white/70 dark:bg-white/5 backdrop-blur-md rounded-2xl border border-slate-200 dark:border-white/10 flex items-center justify-center shadow-md animate-float-slow pointer-events-none">
          <svg className="w-6 h-6 text-sky-500 dark:text-sky-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>

        {/* Floating Growth/Trending Up Icon (Bottom-Left) */}
        <div className="absolute bottom-[5%] left-[22%] z-30 w-10 h-10 bg-indigo-600 text-white rounded-full flex items-center justify-center shadow-md animate-float-slow-reverse pointer-events-none">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
          </svg>
        </div>

        {/* Floating Globe/Sphere Icon (Top-Right) */}
        <div className="absolute top-[28%] right-[-2%] z-10 w-12 h-12 bg-[#ff6b00]/80 text-white rounded-full flex items-center justify-center shadow-lg animate-bounce-slow pointer-events-none">
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
          </svg>
        </div>

        {/* Floating Compliance Speech Bubble (Center) */}
        <div className="absolute top-[40%] right-[38%] z-40 bg-gradient-to-r from-[#ff6b00] to-[#e65c00] text-white text-[10px] sm:text-xs font-black px-3.5 py-2 rounded-2xl shadow-xl animate-float-slow-reverse flex items-center gap-1.5 border border-white/20">
          <span>🔒 100% Compliant</span>
        </div>

        {/* Image 1: Main Top Image (Founder) */}
        <div className="absolute top-[5%] right-[6%] w-[68%] h-[60%] z-10 overflow-hidden shadow-2xl border-4 border-white dark:border-slate-800 bg-slate-900 rounded-[50px] rounded-tl-[110px] rounded-br-[110px] transform hover:scale-[1.02] transition-all duration-500">
          <img
            src="/images/hero_founder.png"
            alt="U.S. Business Founder"
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>

        {/* Image 2: Middle Right Image (Accountant) */}
        <div className="absolute bottom-[8%] right-[0%] w-[48%] h-[48%] z-30 overflow-hidden shadow-2xl border-4 border-white dark:border-slate-800 bg-slate-900 rounded-[40px] rounded-tl-[80px] rounded-br-[80px] transform hover:scale-[1.04] hover:-rotate-1 transition-all duration-500">
          <img
            src="/images/hero_accountant.png"
            alt="Expert Accountant Support"
            className="w-full h-full object-cover"
            loading="lazy"
          />
          {/* Subtle overlay badge inside image */}
          <div className="absolute bottom-3 left-3 right-3 bg-white/95 dark:bg-slate-900/95 rounded-xl p-2 shadow-md">
            <p className="text-[#0f172a] dark:text-white font-bold text-[10px] sm:text-xs leading-none">Expert CPA</p>
            <p className="text-gray-500 dark:text-gray-400 text-[8px] sm:text-[10px] mt-0.5">24/7 Dedicated Support</p>
          </div>
        </div>

        {/* Image 3: Bottom Left Image (Dashboard) */}
        <div className="absolute bottom-[14%] left-[6%] w-[46%] h-[44%] z-20 overflow-hidden shadow-2xl border-4 border-white dark:border-slate-800 bg-slate-900 rounded-[35px] rounded-tr-[70px] rounded-bl-[70px] transform hover:scale-[1.04] hover:rotate-1 transition-all duration-500">
          <img
            src="/images/hero_dashboard.png"
            alt="Financial Dashboard"
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>

      </div>
    </div>
  );
};

export default HeroImageCollage;
